import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Button, InputText, DataTable, Paginator, SkeletonLoader } from 'components';
import { api, humanReadable } from 'utils';
import { locales } from 'constants/index';

import './participants.scss';

/*
 * Participants listing. On load it shows the current List 0 members, paginated.
 * Filtering by AZADEA ID and/or Person ID switches to that member's full
 * history across every snapshot received over time (newest first). Pagination
 * is server-side. Both IDs are shown per row so an ID change over time shows.
 */

type ParticipantRow = {
  studyId: string;
  createdDate: string | null;
  state: string;
  flag: string;
  firstName: string;
  familyName: string;
  azadeaId: string;
  personId: string;
  country: string;
  companyName: string;
  position: string;
  leaveDate: string;
  leaveReason: string;
};

type Params = { page: number; limit: number; azadeaId: string; personId: string };

function fetchParticipants({ page, limit, azadeaId, personId }: Params) {
  return api()
    .get('/list/participants', { params: { page, limit, azadeaId, personId } })
    .then((res) => ({
      list: (res?.data?.participants || []) as ParticipantRow[],
      total: (res?.data?.total || 0) as number,
    }));
}

function DateBody(row: ParticipantRow) {
  return row.createdDate ? humanReadable(row.createdDate) : '-';
}

function NameBody(row: ParticipantRow) {
  return [row.firstName, row.familyName].filter(Boolean).join(' ') || '-';
}

function StateBody(row: ParticipantRow) {
  const modifier =
    row.flag === 'day0'
      ? 'participants-state--current'
      : row.flag === 'invalid'
      ? 'participants-state--rejected'
      : 'participants-state--archived';
  return <span className={`participants-state ${modifier}`}>{row.state}</span>;
}

function LeaveBody(row: ParticipantRow) {
  if (!row.leaveDate && !row.leaveReason) return '-';
  return [row.leaveReason, row.leaveDate].filter(Boolean).join(' – ');
}

const columns = [
  { field: 'createdDate', header: 'Date', body: DateBody, sortable: true },
  { field: 'state', header: 'State', body: StateBody },
  { field: 'name', header: 'Name', body: NameBody },
  { field: 'azadeaId', header: 'AZADEA ID', sortable: true },
  { field: 'personId', header: 'Person ID', sortable: true },
  { field: 'country', header: 'Country', sortable: true },
  { field: 'companyName', header: 'Company', sortable: true },
  { field: 'position', header: 'Position', sortable: true },
  { field: 'leave', header: 'Leave', body: LeaveBody },
  { field: 'studyId', header: 'Study ID' },
];

export default function ListZeroParticipants() {
  // Draft values in the inputs vs. the applied filters that drive the query.
  const [azadeaIdInput, setAzadeaIdInput] = useState('');
  const [personIdInput, setPersonIdInput] = useState('');
  const [azadeaId, setAzadeaId] = useState('');
  const [personId, setPersonId] = useState('');
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(50);

  const page = Math.floor(first / rows) + 1;

  const { data, isFetching, isError, error } = useQuery(
    ['participants', page, rows, azadeaId, personId],
    () => fetchParticipants({ page, limit: rows, azadeaId, personId }),
    { keepPreviousData: true }
  );

  const onSearch = () => {
    setAzadeaId(azadeaIdInput.trim());
    setPersonId(personIdInput.trim());
    setFirst(0);
  };

  const onClear = () => {
    setAzadeaIdInput('');
    setPersonIdInput('');
    setAzadeaId('');
    setPersonId('');
    setFirst(0);
  };

  const onEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') onSearch();
  };

  const onPageChange = (e: any) => {
    setFirst(e.first);
    setRows(e.rows);
  };

  const errMsg =
    (error as any)?.response?.data?.message || (error as any)?.message || 'Search failed.';

  const list = data?.list || [];
  const total = data?.total || 0;

  return (
    <div className="participants">
      <p className="participants__intro">
        Shows the current List 0 members. Filter by AZADEA ID or Person ID to see a member's full
        history over time. Each row is one submission, newest first.
      </p>

      <div className="formgrid grid align-items-end">
        <div className="field sm:col-12 md:col-4 lg:col-3">
          <InputText
            id="azadeaId"
            name="azadeaId"
            placeholder="AZADEA ID"
            value={azadeaIdInput}
            onChange={(e) => setAzadeaIdInput(e.target.value)}
            onKeyDown={onEnter}
          />
        </div>
        <div className="field sm:col-12 md:col-4 lg:col-3">
          <InputText
            id="personId"
            name="personId"
            placeholder="Person ID"
            value={personIdInput}
            onChange={(e) => setPersonIdInput(e.target.value)}
            onKeyDown={onEnter}
          />
        </div>
        <div className="field sm:col-6 md:col-2 lg:col-2">
          <Button label={locales.general.search} onClick={onSearch} />
        </div>
        <div className="field sm:col-6 md:col-2 lg:col-2">
          <Button label="Clear" outlined onClick={onClear} />
        </div>
      </div>

      {isFetching && !data ? (
        <SkeletonLoader height="8rem" />
      ) : isError ? (
        <div className="participants__error">{errMsg}</div>
      ) : (
        <>
          <DataTable
            sortMode="multiple"
            value={list}
            totalRecords={total}
            columns={columns}
            emptyMessage="No participants found"
          />

          {total > 0 && (
            <Paginator
              first={first}
              rows={rows}
              totalRecords={total}
              rowsPerPageOptions={[10, 50, 100]}
              onPageChange={onPageChange}
            />
          )}
        </>
      )}
    </div>
  );
}
